/**
 * Injection seam for the logger door (G1/F5: packages never read
 * `process.env` — the app reads env/build-profile signals once and passes
 * resolved values in via `configureLogger`).
 *
 * The door ships a SAFE DEFAULT so the 800+ eager importers of the `logger`
 * singleton can log from module-evaluation time, before the app bootstrap has
 * configured anything: console-only (dev levels), Datadog transmission off,
 * never throws.
 */

import { type LogContext } from './types';

export interface LoggerConfig {
  /**
   * ON-PREM-GATE (G1): the explicit adapter switch. Transmission to Datadog
   * happens ONLY when this is true — a present/leaked Datadog configuration
   * is never sufficient. App wiring passes
   * `!isOnPrem && !!process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN`.
   */
  enabled: boolean;
  /** App-side `getEnvironment() === ENV_NAME.PROD` — drives the level policy. */
  isProd: boolean;
  /** Record binding (`env`), e.g. 'development' | 'staging' | 'production'. */
  environment: string;
  /** Record binding (`version`) — `formatVersion(releaseRef, releaseSha)`. */
  release: string;
  /** Deployment metadata forwarded to Datadog log context. */
  bundleId?: string;
  gitCommit?: string;
  deploymentId?: string;
  /**
   * Ambient context seam: called on every emitted log record, its result
   * merged into the record (pino + Datadog alike) BENEATH call-site data, so
   * `logger.child({ domain })` bindings and the logged payload always win over
   * an ambient key of the same name.
   *
   * Use it for values no call site can know — active route, user tier, current
   * session id. Static, call-site-scoped data (module / domain tags) belongs in
   * `logger.child(...)` instead: bound once, no per-call cost.
   *
   * Contract:
   * - Runs per log call, so it may read live state — keep it cheap.
   * - Must never throw: a throwing enricher is swallowed and the record is
   *   still logged without ambient context, because every one of the ~800
   *   importers of the `logger` singleton would otherwise inherit the crash.
   * - Skipped entirely when neither sink would emit (see `willEmit` in
   *   ./logger) — on the prod/on-prem hot path it costs nothing.
   * - Must not return PHI: the Datadog egress path does not apply the
   *   `pinoRedactedPaths` list (see ./redaction, TODO ARCFE-679).
   */
  enrich?: () => LogContext;
}

const SAFE_DEFAULT: LoggerConfig = {
  enabled: false,
  isProd: false,
  environment: 'development',
  release: 'dev-local',
};

let current: LoggerConfig = SAFE_DEFAULT;
// Bumped on every (re)configure; logger.ts caches its pino instances per
// generation so a configure call rebuilds them with the new bindings.
let generation = 0;

/**
 * One-time app-bootstrap call (src/utils/runtime-wiring.ts). Logging before
 * this runs degrades to the safe default — console-only, Datadog-silent.
 */
export function configureLogger(config: LoggerConfig): void {
  current = config;
  generation += 1;
}

export function getLoggerConfig(): LoggerConfig {
  return current;
}

export function getLoggerGeneration(): number {
  return generation;
}

export function __resetLoggerForTests(): void {
  current = SAFE_DEFAULT;
  generation += 1;
}
